import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useAuthStore } from '../../auth/store';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useConfirm } from '../../components/confirm';
import { fmtLocalDate } from '../../utils/datetime';
import { AvaKnowledgeDialog } from './AvaKnowledgeDialog';
import { AvaMyWorkCard, type MyWork } from './AvaMyWorkCard';
import { useNavPalette } from '../../components/hotkeys';
import './ava.css';

/* AVA Insight — Standard mode only. Every answer comes from the ALPACA DB /
   Help Center (no AI). The page holds NO answer logic: every question goes to
   GET /api/v1/ava/ask, which parses it, routes it (Help article · job / sample /
   report status · lab queue count) and decides whether an article really
   answers it — the page only renders the reply. Layout ports GoLive's full-page
   AVA: left sidebar (ALPACA LIMS · +New Chat · TODAY · BROWSE HELP with 6
   categories) + main (header · content · input) + hamburger menu. The Lab
   Dashboard KPI strip was removed on owner request (2026-10-01) — ask AVA
   "how many samples are overdue" instead. */

interface BrowseCounts { faq: number; videos: number; objectManuals: number; flowcharts: number; chempedia: number; alpacaExplore: number; }
interface Hit { oid: string; topic: string; preview: string; category: string | null; }
/** A screen (or menu folder) AVA points to; `id` is the page that "Open" opens. */
interface ScreenHit { id: string; name: string; path: string; hasForm: boolean; isMenu: boolean; }
interface FormField { key: string; label: string; type: string; required: boolean; readOnly: boolean; section: string | null; choices: string[] | null; choicesFromServer: boolean; }
interface FormAnswer { screenId: string; screenName: string; path: string; entity: string; fields: FormField[]; }
interface WorkflowChain { module: string; flow: string; steps: string[]; }
interface WorkflowNext { module: string; flow: string; step: string; next: string | null; }
interface Capability { title: string; description: string; examples: string[]; }
interface JobStatus {
  jobId: string; client: string | null; project: string | null;
  received: string | null; due: string | null; sampleCount: number;
  testCount: number; resultsEntered: number; reported: number;
  samplesOnHold: number; testsOnHold: number; currentStatus: string | null;
  progressPercent: number; overdue: boolean;
}
interface SampleTest { sampleId: string; clientSampleId: string | null; test: string | null; method: string | null; status: string | null; onHold: boolean; }
interface ReportRow { reportId: string; jobId: string | null; status: string | null; reportedDate: string | null; }
interface WorklistJob { jobId: string; sampleCount: number; due: string | null; }
interface Worklist { total: number; unit: string; jobCount: number; jobs: WorklistJob[]; }
interface MediaLink { url: string; fileName: string; kind: 'video' | 'pdf' | 'file'; }
interface ArticleDto { oid: string; topic: string; html: string | null; plain: string | null; category: string | null; size: string | null; media?: MediaLink | null; }
/** GET /api/v1/ava/ask reply — `kind` says which payload is filled. */
interface AvaAnswer {
  kind: 'article' | 'suggestions' | 'notFound' | 'job' | 'sample' | 'report' | 'worklist' | 'faq' | 'categories'
    | 'screen' | 'form' | 'workflow' | 'capabilities' | 'howto' | 'mywork';
  message: string;
  article: ArticleDto | null;
  media: MediaLink | null;
  related: Hit[];
  job: JobStatus | null;
  samples: SampleTest[];
  reports: ReportRow[];
  worklist: Worklist | null;
  categories: { name: string; count: number }[];
  /** "Did you mean…?" — correctly-worded questions to click and ask. */
  suggestedQuestions: string[];
  // AVA knowledge answers (Step 2) — absent on an older API, hence optional.
  screens?: ScreenHit[];
  form?: FormAnswer | null;
  workflows?: WorkflowChain[];
  nextSteps?: WorkflowNext[];
  capabilities?: Capability[];
  /** Numbered steps composed from AVA's knowledge ("howto"). */
  steps?: string[];
  /** The user's own work queues ("mywork"). */
  myWork?: MyWork | null;
  /** Step 5 — what this answer was about; sent back with the next question so a
   *  follow-up ("and for TOC?") is understood. Opaque to the page. */
  context?: AvaContextNote | null;
  /** Step 5 — the complete question a follow-up was read as ("I took that as …"). */
  interpreted?: string | null;
}
/** The server's context note (AvaContext). The page only stores and returns it. */
type AvaContextNote = Record<string, unknown>;
type Msg =
  | { id: number; role: 'user' | 'bot'; kind: 'text'; text: string }
  | { id: number; role: 'bot'; kind: 'note'; text: string }
  /** One answer, shown as ONE message: its text, steps, screens, chips … together. */
  | { id: number; role: 'bot'; kind: 'group'; parts: Msg[] }
  | { id: number; role: 'bot'; kind: 'job'; job: JobStatus }
  | { id: number; role: 'bot'; kind: 'hits'; title: string; hits: Hit[] }
  | { id: number; role: 'bot'; kind: 'samples'; rows: SampleTest[] }
  | { id: number; role: 'bot'; kind: 'reports'; rows: ReportRow[] }
  | { id: number; role: 'bot'; kind: 'worklist'; list: Worklist }
  | { id: number; role: 'bot'; kind: 'suggest'; questions: string[]; title?: string }
  | { id: number; role: 'bot'; kind: 'screens'; screens: ScreenHit[] }
  | { id: number; role: 'bot'; kind: 'form'; form: FormAnswer }
  | { id: number; role: 'bot'; kind: 'workflow'; chains: WorkflowChain[]; next: WorkflowNext[] }
  | { id: number; role: 'bot'; kind: 'capabilities'; items: Capability[] }
  | { id: number; role: 'bot'; kind: 'steps'; steps: string[] }
  | { id: number; role: 'bot'; kind: 'mywork'; work: MyWork }
  | { id: number; role: 'bot'; kind: 'article'; oid: string | null; topic: string; html: string | null; text: string; category: string | null; size: string | null; media: MediaLink | null; stub?: boolean };

/** One chat conversation — the unit of history, like ChatGPT / Claude. */
interface Conversation {
  id: string; title: string; updatedAt: number; msgs: Msg[];
  /** The last answer's context note, so a reopened chat can be followed up (Step 5). */
  context?: AvaContextNote | null;
}

/** Label over follow-up questions after a real answer ("Did you mean" is for near misses). */
const FOLLOW_UP = 'You can also ask:';

const SECTIONS: { key: keyof BrowseCounts; label: string; cat: string; icon: string }[] = [
  { key: 'faq',           label: 'FAQ',            cat: 'FAQ',            icon: 'ti-help-circle' },
  { key: 'videos',        label: 'Videos',         cat: 'Videos',         icon: 'ti-video' },
  { key: 'objectManuals', label: 'Object Manuals', cat: 'Object Manuals', icon: 'ti-book-2' },
  { key: 'flowcharts',    label: 'Flowcharts',     cat: 'Flowcharts',     icon: 'ti-affiliate' },
  { key: 'chempedia',     label: 'Chempedia',      cat: 'Chempedia',      icon: 'ti-flask-2' },
  { key: 'alpacaExplore', label: 'ALPACA Explore', cat: 'ALPACA Explore', icon: 'ti-compass' },
];

// token-exempt: user-selectable accent SWATCHES — these literal hexes ARE the
// palette values persisted to localStorage + applied as --ava-accent, not app chrome.
const COLORS = ['#e07b00','#dc2626','#16a34a','#7c3aed','#2563eb'] as const;
const COLOR_KEY = 'ava.color';
const DARK_KEY  = 'ava.dark';
// Conversation history (one entry per CONVERSATION, titled by its first
// question) — per-viewer convenience in localStorage, per user.
const CONV_KEY_PREFIX = 'ava.conversations.';
const LEGACY_HIST_KEY = 'ava.history';   // old one-entry-per-question list (dropped)
const MAX_CONVERSATIONS = 30;
const MAX_MSGS_PER_CONVERSATION = 200;

const newConversationId = () =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

/** A message as it is SAVED: an article keeps only its id/title ("Open article"
 *  card on restore) — its HTML can be megabytes and its media link expires. */
const toStored = (m: Msg): Msg =>
  m.kind === 'article'
    ? { ...m, html: null, text: '', media: null, stub: true }
    : m.kind === 'group'
      ? { ...m, parts: m.parts.map(toStored) }
      : m;

const loadConversations = (key: string): Conversation[] => {
  try {
    const raw = JSON.parse(localStorage.getItem(key) || '[]') as Conversation[];
    return Array.isArray(raw) ? raw.filter((c) => c && c.id && Array.isArray(c.msgs)) : [];
  } catch { return []; }
};
const saveConversations = (key: string, list: Conversation[]) => {
  try { localStorage.setItem(key, JSON.stringify(list.slice(0, MAX_CONVERSATIONS))); }
  catch { /* storage full / blocked — history is a convenience, the chat still works */ }
};

/** ChatGPT-style sidebar buckets. */
const conversationGroup = (t: number): string => {
  const day = 86_400_000;
  const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
  const t0 = startOfToday.getTime();
  if (t >= t0) return 'TODAY';
  if (t >= t0 - day) return 'YESTERDAY';
  if (t >= t0 - 7 * day) return 'PREVIOUS 7 DAYS';
  return 'OLDER';
};
const GROUP_ORDER = ['TODAY', 'YESTERDAY', 'PREVIOUS 7 DAYS', 'OLDER'];

// Shared helper (§14b.6): local tz date, force-UTC inside; raw fallback.
const fmtDate = (s: string | null) => (s ? (fmtLocalDate(s) || s) : '—');
const stripHtml = (h: string) =>
  h.replace(/<style[\s\S]*?<\/style>/gi, '').replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/\s+/g, ' ').trim();

/**
 * Sanitize article HTML so we can render it inside the chat: keep formatting
 * + flowchart images (base64 data URIs), strip scripts/styles/event handlers
 * and anything that could execute. Lighter than DOMPurify — no extra dep.
 */
const ALLOWED_TAGS = new Set([
  'p','br','div','span','strong','b','em','i','u','small','sub','sup','code','pre',
  'ol','ul','li','h1','h2','h3','h4','h5','h6','blockquote','hr',
  'table','thead','tbody','tfoot','tr','td','th','caption',
  'img','a','figure','figcaption',
]);
const ALLOWED_ATTRS = new Set([
  'href','src','alt','title','width','height','rowspan','colspan','style','class','target','rel',
]);
const sanitizeHtml = (raw: string): string => {
  if (!raw) return '';
  try {
    const doc = new DOMParser().parseFromString(raw, 'text/html');
    const walk = (node: Element) => {
      // Snapshot children — we may remove some.
      const children = Array.from(node.children);
      for (const el of children) {
        const tag = el.tagName.toLowerCase();
        if (!ALLOWED_TAGS.has(tag)) { el.remove(); continue; }
        // Strip non-whitelisted attrs (and dangerous href/src schemes).
        for (const attr of Array.from(el.attributes)) {
          const name = attr.name.toLowerCase();
          const val = attr.value || '';
          if (!ALLOWED_ATTRS.has(name)) { el.removeAttribute(attr.name); continue; }
          if ((name === 'href' || name === 'src')) {
            const lower = val.trim().toLowerCase();
            const ok = lower.startsWith('http:') || lower.startsWith('https:') ||
                       lower.startsWith('data:image/') || lower.startsWith('mailto:') ||
                       lower.startsWith('#') || lower.startsWith('/');
            if (!ok) { el.removeAttribute(attr.name); continue; }
          }
          // No inline event handlers (defense in depth — already excluded above).
          if (name.startsWith('on')) el.removeAttribute(attr.name);
        }
        // Force external links to open safely.
        if (tag === 'a') { el.setAttribute('target', '_blank'); el.setAttribute('rel', 'noopener noreferrer'); }
        // Force img to fit container.
        if (tag === 'img') el.setAttribute('style', 'max-width:100%;height:auto;border-radius:6px;');
        walk(el);
      }
    };
    walk(doc.body);
    return doc.body.innerHTML;
  } catch { return ''; }
};
const initials = (n?: string) =>
  (n || 'You').trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase() || 'YOU';

/**
 * Greetings + small-talk detector (no AI). Returns the EXACT canned reply the
 * GoLive ALPACA AVA gives (ported word-for-word from
 * BTLIMS.Web/ava/ChatBotHandler.Actions.cs · HandleChatMessage), or null if
 * the message is a real question that should go through the help/job pipeline.
 * Replies are intentionally short — no name padding — exactly like GoLive.
 */
function detectSmallTalk(low: string): string | null {
  const m = low.trim().replace(/[!.?,]+$/g, '');

  // Greeting — GoLive: hello / hi / hey / good morning|afternoon|evening.
  // GoLive always returns a time-based greeting (the plain "Hello! 👋" branch
  // is unreachable since every hour falls into one of the three ranges).
  const isGreeting =
    m === 'hi' ||
    /^(hi+|hey+|hello|heya|howdy|hola|namaste|namaskar|yo)\b/.test(m) ||
    /\bgood\s+(morning|afternoon|evening)\b/.test(m);
  if (isGreeting) {
    const hour = new Date().getHours();
    if (m.includes('morning')   || (hour >= 5 && hour < 12)) return 'Hello, Good morning! \u{1F60A}';
    if (m.includes('afternoon') || (hour >= 12 && hour < 17)) return 'Hello, Good afternoon! \u{1F60A}';
    return 'Hello, Good evening! \u{1F60A}';
  }
  // "How are you" → GoLive exact
  if (/\b(how are you|how r you|how're you|how r u|how are u|how is it going|how'?s it going)\b/.test(m))
    return "I'm doing great, thanks for asking! \u{1F60A} How about you?";
  // Reply to "How about you?" (user says they're good/fine/great) — GoLive's
  // exact accepted list.
  if ([
    'i am good',"i'm good",'im good','i am fine',"i'm fine",'im fine',
    'i am great',"i'm great",'im great',"i'm doing good","i'm doing fine","i'm doing great",
    'i am doing good','i am doing fine','i am doing great','good','fine','great','not bad',
    'all good','doing good','doing fine','doing great','pretty good','i am ok',"i'm ok",'im ok','ok','okay',
  ].includes(m))
    return "That's great to hear! \u{1F60A}";
  // Thanks → GoLive exact
  if (m === 'thank you' || m === 'thanks' || m === 'thank u' || m === 'ty' ||
      m.startsWith('thanks') || m.startsWith('thank you'))
    return "You're welcome! \u{1F60A}";
  // Bye → GoLive exact
  if (/^(bye|goodbye|good\s*bye|see you|see ya|cya|later|take care|good night)\b/.test(m))
    return 'Bye! Have a great day! \u{1F44B}';
  // Who are you → GoLive exact
  if (/\b(who are you|what are you|what'?s ava|what is ava|who is ava|about ava|tell me about yourself|your name)\b/.test(m))
    return "I'm AVA, ALPACA LIMS virtual assistant by BTSOFT! \u{1F60A} I can help you with sample tracking, job status, data queries, workflows, and anything LIMS-related.";
  return null;
}

let _id = 1;
const nextId = () => _id++;

/* AVA mascot avatar — the GoLive artwork itself (BTLIMS.Web/Images/ava-avatar.png,
   the image the GoLive full-page AVA renders), copied to web/public. If the file
   is ever missing we fall back to the robot glyph so the UI never breaks. */
const AVA_AVATAR_SRC = `${import.meta.env.BASE_URL}ava-avatar.png`;
function AvaFace() {
  const [failed, setFailed] = useState(false);
  if (failed) return <i className="ti ti-robot" />;
  return (
    <img
      src={AVA_AVATAR_SRC}
      alt="AVA"
      className="ava-avatar-img"
      onError={() => setFailed(true)}
    />
  );
}

export function AvaInsightPage() {
  const notify = useNotify();
  const user = useAuthStore((s) => s.user);
  const isAdmin = useAuthStore((s) => s.permissions?.isAdmin === true);
  const roles = useAuthStore((s) => s.permissions?.roles ?? '');
  // Administrators, LIMS Administrators (role — the Users-form tick already makes
  // isAdmin true) and the service account. Mirrors the server's
  // AvaAccessRules.LimsAdministratorRole; the server checks it again.
  const isLimsAdmin = roles.split(',').some((r) => r.trim().toLowerCase() === 'lims administrator');
  const canSeeKnowledge = isAdmin || isLimsAdmin || (user?.name ?? '').trim().toLowerCase() === 'service';
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  // The server only names screens this user may open (Step 3 — the same rule as
  // the sidebar), so every screen card gets an "Open". The palette's own run()
  // is used when it lists the page; otherwise (e.g. To Do List views, which the
  // Ctrl+K palette leaves out on purpose) the shell's ai:navigate event opens it.
  const openable = useNavPalette((s) => s.items);
  const openScreen = (id: string) => {
    const item = openable.find((i) => i.id === id);
    if (item) item.run();
    else window.dispatchEvent(new CustomEvent('ai:navigate', { detail: { page: id } }));
  };
  const av = initials(user?.name);

  const [counts, setCounts] = useState<BrowseCounts | null>(null);
  // "My work" on the welcome screen (Step 4) — counted server-side by the queue
  // screens' own readers; loaded once when AVA opens.
  const [myWork, setMyWork] = useState<MyWork | null>(null);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const { confirm } = useConfirm();
  const convKey = CONV_KEY_PREFIX + (user?.name || 'anonymous');
  const [conversations, setConversations] = useState<Conversation[]>(() => loadConversations(convKey));
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  const activeConvRef = useRef<string | null>(null);
  activeConvRef.current = activeConvId;
  /** Step 5 — the last answer's context note in the open conversation (only the last one). */
  const contextRef = useRef<AvaContextNote | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem(DARK_KEY) === '1');
  const [accent, setAccent] = useState(() => localStorage.getItem(COLOR_KEY) || COLORS[0]);
  const [listening, setListening] = useState(false);
  const [voiceReply, setVoiceReply] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const convoRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const recogRef = useRef<unknown>(null);

  // ── Initial load: browse-help badge counts ──
  useEffect(() => {
    let live = true;
    api.get('/api/v1/ava/browse-counts')
      .then((r) => { if (live) setCounts(r.data?.data ?? null); })
      .catch(() => { if (live) setCounts(null); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    let live = true;
    api.get('/api/v1/ava/my-work')
      .then((r) => { if (live) setMyWork(r.data?.data?.work ?? null); })
      .catch(() => { if (live) setMyWork(null); });   // the welcome screen works without it
    return () => { live = false; };
  }, []);

  // ── Conversation history ──
  // Drop the old one-line-per-question list once.
  useEffect(() => { try { localStorage.removeItem(LEGACY_HIST_KEY); } catch { /* ignore */ } }, []);
  // Reload when the signed-in user changes (history is per user).
  useEffect(() => { setConversations(loadConversations(convKey)); setActiveConvId(null); }, [convKey]);
  // Every change to the OPEN conversation is written back, newest conversation first.
  useEffect(() => {
    if (!activeConvId || msgs.length === 0) return;
    setConversations((list) => {
      const current = list.find((c) => c.id === activeConvId);
      if (!current) return list;
      const updated: Conversation = {
        ...current,
        updatedAt: Date.now(),
        msgs: msgs.slice(-MAX_MSGS_PER_CONVERSATION).map(toStored),
        context: contextRef.current,
      };
      const next = [updated, ...list.filter((c) => c.id !== activeConvId)];
      saveConversations(convKey, next);
      return next;
    });
  }, [msgs, activeConvId, convKey]);
  useEffect(() => { localStorage.setItem(DARK_KEY, dark ? '1' : '0'); }, [dark]);
  useEffect(() => { localStorage.setItem(COLOR_KEY, accent); }, [accent]);

  // ── Auto-scroll conversation ──
  useEffect(() => {
    convoRef.current?.scrollTo({ top: convoRef.current.scrollHeight, behavior: 'smooth' });
  }, [msgs]);

  // ── Release voice resources on unmount (memory-leak fix 2026-07-12) ──
  // A live SpeechRecognition session or a queued speechSynthesis utterance keeps
  // running after the page is navigated away from — stop both when we unmount.
  useEffect(() => {
    return () => {
      try { (recogRef.current as { stop: () => void } | null)?.stop(); } catch { /* ignore */ }
      recogRef.current = null;
      try { if ('speechSynthesis' in window) window.speechSynthesis.cancel(); } catch { /* ignore */ }
    };
  }, []);

  // ── Click-outside closes menu ──
  useEffect(() => {
    if (!menuOpen) return;
    const h = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menuOpen]);

  const push = (m: Msg) => setMsgs((x) => [...x, m]);

  // ── Speak reply via Web Speech API (no AI; browser-native) ──
  // AVA uses a FEMALE voice. We pick the best available one on the platform
  // (Windows Zira / Microsoft Eva, macOS Samantha / Victoria, Google UK
  // English Female, Google US English, etc.) and fall back to any
  // `name` containing "female"/voice marked en-* with female pitch.
  const pickFemaleVoice = (): SpeechSynthesisVoice | null => {
    if (!('speechSynthesis' in window)) return null;
    const all = window.speechSynthesis.getVoices();
    if (!all.length) return null;
    const preferEn = (v: SpeechSynthesisVoice) => v.lang?.toLowerCase().startsWith('en');
    const namePreferences = [
      /Zira/i, /Eva/i, /Aria/i, /Jenny/i, /Samantha/i, /Victoria/i, /Karen/i,
      /Susan/i, /Allison/i, /Ava/i, /Serena/i, /Tessa/i, /Moira/i,
      /Google UK English Female/i, /Google US English/i, /female/i,
    ];
    for (const re of namePreferences) {
      const v = all.find((x) => preferEn(x) && re.test(x.name));
      if (v) return v;
    }
    // Last resort: any English voice that isn't obviously male.
    const malePat = /(David|Mark|Daniel|James|Alex|Fred|Mike|Tom|Paul|Adam|Brian)/i;
    return all.find((v) => preferEn(v) && !malePat.test(v.name)) ?? all[0] ?? null;
  };
  const femaleVoiceRef = useRef<SpeechSynthesisVoice | null>(null);
  useEffect(() => {
    if (!('speechSynthesis' in window)) return;
    const load = () => { femaleVoiceRef.current = pickFemaleVoice(); };
    load();
    // Voices may load asynchronously in Chrome — listen for the event.
    window.speechSynthesis.onvoiceschanged = load;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  const speak = (text: string) => {
    if (!voiceReply || !('speechSynthesis' in window)) return;
    try {
      const u = new SpeechSynthesisUtterance(text.slice(0, 500));
      const voice = femaleVoiceRef.current ?? pickFemaleVoice();
      if (voice) u.voice = voice;
      u.pitch = 1.05;   // very slight feminine pitch lift on platforms with
      u.rate = 1.0;     //   neutral-pitched voices
      u.lang = 'en-US';
      window.speechSynthesis.cancel();      // stop any earlier utterance
      window.speechSynthesis.speak(u);
    } catch { /* ignore */ }
  };

  const articleMsg = (a: ArticleDto, fallbackTopic: string, media: MediaLink | null = a.media ?? null): Msg => {
    const html = (a.html ? sanitizeHtml(a.html) : '') || null;
    return {
      id: nextId(), role: 'bot', kind: 'article',
      oid: a.oid ?? null,
      topic: a.topic ?? fallbackTopic,
      html,
      // Plain text is only a fallback for articles WITHOUT html (the server now
      // omits it otherwise) - never strip an 18 MB manual just to discard it.
      text: html ? '' : ((a.plain && a.plain.trim()) || (a.html ? stripHtml(a.html) : '')),
      category: a.category ?? null,
      size: a.size ?? null,
      media,
    };
  };

  const openArticle = async (oid: string, topic: string) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/api/v1/ava/help/article/${oid}`);
      const a = data?.data as ArticleDto | undefined;
      if (!a) throw new Error('Article not found');
      push(articleMsg(a, topic));
      speak(`Opening ${a.topic ?? topic}`);
    } catch { push({ id: nextId(), role: 'bot', kind: 'text', text: 'Could not open that article.' }); }
    finally { setBusy(false); }
  };

  // Section lists already fetched this visit - a repeat click is instant (the
  // server also caches them for 10 minutes).
  const sectionCache = useRef(new Map<string, Hit[]>());
  const openSection = async (cat: string, label: string) => {
    setActiveSection(label);
    setActiveConvId(null);   // browsing a section is a fresh view, not part of a saved conversation
    contextRef.current = null;
    const cached = sectionCache.current.get(cat);
    if (cached) {
      setMsgs([{ id: nextId(), role: 'bot', kind: 'hits', title: `${label} (${cached.length})`, hits: cached }]);
      return;
    }
    setMsgs([{ id: nextId(), role: 'bot', kind: 'text', text: `Browsing ${label} articles.` }]);
    setBusy(true);
    try {
      const { data } = await api.get(`/api/v1/ava/help/category/${encodeURIComponent(cat)}`);
      const hits = (data?.data ?? []) as Hit[];
      sectionCache.current.set(cat, hits);
      setMsgs([{ id: nextId(), role: 'bot', kind: 'hits', title: `${label} (${hits.length})`, hits }]);
    } catch { push({ id: nextId(), role: 'bot', kind: 'text', text: `Could not load ${label}.` }); }
    finally { setBusy(false); }
  };

  /** Turns one server answer into chat messages. Rendering only — no decisions. */
  const renderAnswer = (a: AvaAnswer | undefined) => {
    if (!a) { push({ id: nextId(), role: 'bot', kind: 'text', text: 'Sorry, I got an empty reply.' }); return; }
    // Everything this answer says goes into ONE message (user request 2026-10-10).
    const parts: Msg[] = [];
    const add = (m: Msg) => { parts.push(m); };
    // A follow-up was read as a complete question: say how, so a wrong guess is easy to spot.
    if (a.interpreted) add({ id: nextId(), role: 'bot', kind: 'note', text: `I took that as: "${a.interpreted}"` });
    const say = (text: string) => { if (text) add({ id: nextId(), role: 'bot', kind: 'text', text }); };
    switch (a.kind) {
      case 'screen':
        say(a.message);
        if (a.screens?.length) add({ id: nextId(), role: 'bot', kind: 'screens', screens: a.screens });
        if (a.suggestedQuestions?.length) add({ id: nextId(), role: 'bot', kind: 'suggest', questions: a.suggestedQuestions, title: FOLLOW_UP });
        if (a.related.length) add({ id: nextId(), role: 'bot', kind: 'hits', title: 'Related help articles', hits: a.related });
        speak(a.message);
        break;
      case 'mywork':
        say(a.message);
        if (a.myWork) add({ id: nextId(), role: 'bot', kind: 'mywork', work: a.myWork });
        speak(a.message);
        break;
      case 'howto':
        say(a.message);
        if (a.steps?.length) add({ id: nextId(), role: 'bot', kind: 'steps', steps: a.steps });
        if (a.screens?.length) add({ id: nextId(), role: 'bot', kind: 'screens', screens: a.screens });
        if (a.form) add({ id: nextId(), role: 'bot', kind: 'form', form: a.form });
        if (a.suggestedQuestions?.length) add({ id: nextId(), role: 'bot', kind: 'suggest', questions: a.suggestedQuestions, title: FOLLOW_UP });
        if (a.related.length) add({ id: nextId(), role: 'bot', kind: 'hits', title: 'More in the Help Center', hits: a.related });
        speak(`${a.message} ${(a.steps ?? []).join(' ')}`);
        break;
      case 'form':
        say(a.message);
        if (a.form) add({ id: nextId(), role: 'bot', kind: 'form', form: a.form });
        if (a.screens?.length) add({ id: nextId(), role: 'bot', kind: 'screens', screens: a.screens });
        speak(a.message);
        break;
      case 'workflow':
        say(a.message);
        add({ id: nextId(), role: 'bot', kind: 'workflow', chains: a.workflows ?? [], next: a.nextSteps ?? [] });
        if (a.suggestedQuestions?.length) add({ id: nextId(), role: 'bot', kind: 'suggest', questions: a.suggestedQuestions, title: FOLLOW_UP });
        speak(a.message);
        break;
      case 'capabilities':
        say(a.message);
        if (a.capabilities?.length) add({ id: nextId(), role: 'bot', kind: 'capabilities', items: a.capabilities });
        break;
      case 'article':
        say(a.message);
        if (a.article) add(articleMsg(a.article, a.article.topic, a.media));
        if (a.screens?.length) add({ id: nextId(), role: 'bot', kind: 'screens', screens: a.screens });
        if (a.related.length) add({ id: nextId(), role: 'bot', kind: 'hits', title: 'Related articles', hits: a.related });
        speak(a.article?.topic ?? a.message);
        break;
      case 'suggestions':
        say(a.message);
        if (a.suggestedQuestions?.length) add({ id: nextId(), role: 'bot', kind: 'suggest', questions: a.suggestedQuestions });
        {
          // The server builds the suggested questions from the FIRST related
          // topics, so list only the rest as cards — each item shows once.
          const rest = a.related.slice(a.suggestedQuestions?.length ?? 0);
          if (rest.length) add({ id: nextId(), role: 'bot', kind: 'hits', title: a.suggestedQuestions?.length ? 'More related topics' : 'Related topics', hits: rest });
        }
        speak(a.message);
        break;
      case 'faq':
        add({ id: nextId(), role: 'bot', kind: 'hits', title: 'Frequently asked', hits: a.related });
        break;
      case 'categories':
        say(`${a.message}\n${a.categories.map((c) => `• ${c.name} (${c.count})`).join('\n')}\n\nType a topic to search, or click a section in the sidebar.`);
        break;
      case 'job':
        if (a.job) add({ id: nextId(), role: 'bot', kind: 'job', job: a.job });
        speak(a.message);
        break;
      case 'sample':
        say(a.message);
        add({ id: nextId(), role: 'bot', kind: 'samples', rows: a.samples });
        speak(a.message);
        break;
      case 'report':
        say(a.message);
        add({ id: nextId(), role: 'bot', kind: 'reports', rows: a.reports });
        speak(a.message);
        break;
      case 'worklist':
        say(a.message);
        if (a.worklist?.jobs.length) add({ id: nextId(), role: 'bot', kind: 'worklist', list: a.worklist });
        speak(a.message);
        break;
      default:
        say(a.message);
        if (a.suggestedQuestions?.length) add({ id: nextId(), role: 'bot', kind: 'suggest', questions: a.suggestedQuestions });
        speak(a.message);
    }
    if (parts.length === 1) push(parts[0]!);
    else if (parts.length > 1) push({ id: nextId(), role: 'bot', kind: 'group', parts });
  };

  const ask = async (raw: string) => {
    const q = raw.trim();
    if (!q || busy) return;
    // First question of a chat opens a NEW conversation titled by it; later
    // questions continue the open one (one history entry per conversation).
    if (!activeConvRef.current) {
      contextRef.current = null;   // a new conversation starts without a subject
      const conv: Conversation = { id: newConversationId(), title: q.length > 60 ? `${q.slice(0, 57)}…` : q, updatedAt: Date.now(), msgs: [] };
      activeConvRef.current = conv.id;
      setConversations((list) => [conv, ...list]);
      setActiveConvId(conv.id);
    }
    push({ id: nextId(), role: 'user', kind: 'text', text: q });
    setInput('');
    setActiveSection(null);
    setBusy(true);
    try {
      // Greetings / small talk are answered here (the time-of-day greeting needs
      // the viewer's clock). EVERY real question goes to the server, which owns
      // the routing and decides whether an article genuinely answers it.
      const smalltalk = detectSmallTalk(q.toLowerCase());
      if (smalltalk) {
        push({ id: nextId(), role: 'bot', kind: 'text', text: smalltalk });
        speak(smalltalk);
      } else {
        // The last answer's context travels with the question (Step 5): the server
        // reads "and for TOC?" as a full question. It keeps nothing between questions.
        const { data } = await api.post('/api/v1/ava/ask', { q, context: contextRef.current });
        const answer = data?.data as AvaAnswer | undefined;
        contextRef.current = answer?.context ?? null;
        renderAnswer(answer);
      }
    } catch {
      push({ id: nextId(), role: 'bot', kind: 'text', text: 'Sorry, I hit an error reaching the ALPACA database. Please try again.' });
    } finally { setBusy(false); }
  };

  // ── Voice Chat (Web Speech API: speech-to-text) ──
  const toggleVoiceChat = () => {
    const SR = (window as unknown as { SpeechRecognition?: unknown; webkitSpeechRecognition?: unknown });
    const Recog = (SR.SpeechRecognition ?? SR.webkitSpeechRecognition) as undefined | (new () => {
      lang: string; interimResults: boolean; continuous: boolean;
      start: () => void; stop: () => void;
      onresult: (e: { results: { 0: { transcript: string } }[] }) => void;
      onend: () => void; onerror: () => void;
    });
    if (!Recog) { notify.warning('Voice Chat needs Chrome / Edge.'); return; }
    if (listening) {
      try { (recogRef.current as { stop: () => void } | null)?.stop(); } catch { /* ignore */ }
      setListening(false);
      return;
    }
    const r = new Recog();
    r.lang = 'en-US'; r.interimResults = false; r.continuous = false;
    r.onresult = (e) => { const t = e.results[0]![0]!.transcript; setInput(t); ask(t); };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    recogRef.current = r;
    r.start();
    setListening(true);
  };

  const newChat = () => {
    contextRef.current = null;
    setMsgs([]);
    setActiveConvId(null);
    setActiveSection(null);
    setInput('');
    setMenuOpen(false);
  };
  /** Reopens a saved conversation exactly as it was (fresh ids for React keys). */
  const openConversation = (c: Conversation) => {
    setActiveSection(null);
    setActiveConvId(c.id);
    contextRef.current = c.context ?? null;
    setMsgs(c.msgs.map((m) => ({ ...m, id: nextId() }) as Msg));
    setMenuOpen(false);
  };
  const deleteConversation = async (c: Conversation) => {
    const ok = await confirm({ title: 'Delete conversation', message: `Delete "${c.title}" from your chat history?`, tone: 'danger' });
    if (!ok) return;
    setConversations((list) => {
      const next = list.filter((x) => x.id !== c.id);
      saveConversations(convKey, next);
      return next;
    });
    if (activeConvId === c.id) newChat();
  };
  const clearChat = () => { newChat(); };
  const refreshAva = () => {
    api.get('/api/v1/ava/browse-counts')
      .then((r) => setCounts(r.data?.data ?? null))
      .catch(() => setCounts(null));
    setMenuOpen(false);
  };

  // ── Theme CSS vars on container ──
  const themeStyle: CSSProperties = {
    ['--ava-accent' as string]: accent,
  };

  const showWelcome = msgs.length === 0;

  /** One part of an answer (text, steps, screens, chips, cards …). */
  const renderPart = (m: Msg) => (
    <>
      {m.kind === 'text' && <div className="ava-msg-bubble">{m.text}</div>}
      {m.kind === 'note' && <div className="ava-msg-note"><i className="ti ti-corner-down-right" /> {m.text}</div>}
      {m.kind === 'suggest' && (
        <div className="ava-suggest">
          <div className="ava-suggest-title">{m.title ?? 'Did you mean:'}</div>
          <div className="ava-suggest-list">
            {m.questions.map((q) => (
              <button key={q} type="button" className="ava-suggest-chip" onClick={() => void ask(q)} disabled={busy}>
                <i className="ti ti-message-question" /> {q}
              </button>
            ))}
          </div>
        </div>
      )}
      {m.kind === 'article' && m.stub && (
        <button type="button" className="ava-hit" onClick={() => m.oid && void openArticle(m.oid, m.topic)} disabled={!m.oid || busy}>
          <div className="ava-hit-topic"><i className="ti ti-file-text" /> {m.topic}</div>
          <div className="ava-hit-prev">Open this article again</div>
          {m.category && <div className="ava-hit-cat"><i className="ti ti-tag" /> {m.category}</div>}
        </button>
      )}
      {m.kind === 'article' && !m.stub && (
        <div className="ava-article">
          <h4>{m.topic}</h4>
          {m.category && <div className="ava-article-cat"><i className="ti ti-tag" /> {m.category}</div>}
          {/* The article's own video / PDF, streamed from the Help Center
              database (range requests, so seeking works). */}
          {m.media?.kind === 'video' && (
            <video className="ava-media-video" src={m.media.url} controls preload="metadata" playsInline>
              <a href={m.media.url} target="_blank" rel="noopener noreferrer">Open the video</a>
            </video>
          )}
          {m.media?.kind === 'pdf' && (
            <iframe className="ava-media-pdf" src={m.media.url} title={m.media.fileName} />
          )}
          {m.media && (
            <a className="ava-media-link" href={m.media.url} target="_blank" rel="noopener noreferrer">
              <i className={`ti ${m.media.kind === 'file' ? 'ti-download' : 'ti-external-link'}`} />
              {m.media.kind === 'file' ? `Download ${m.media.fileName}` : `Open ${m.media.fileName} in a new tab`}
            </a>
          )}
          {m.media ? (
            m.html ? <div className="ava-article-body ava-article-html" dangerouslySetInnerHTML={{ __html: m.html }} />
              : m.text ? <div className="ava-article-body">{m.text}</div> : null
          ) : m.category === 'Videos' && !m.html && !m.text ? (
            <div className="ava-article-novideo">
              <i className="ti ti-video-off" />
              <div>
                <strong>This video is not available here.</strong>
                <div className="ava-article-novideo-sub">
                  The video file for <em>{m.topic}</em> has not been copied into this ALPACA database
                  yet - you can watch it in the Help Center (book icon, top right).
                  {m.size && <> Recorded size: <strong>{m.size}</strong>.</>}
                </div>
              </div>
            </div>
          ) : m.html ? (
            <div className="ava-article-body ava-article-html"
              dangerouslySetInnerHTML={{ __html: m.html }} />
          ) : (
            <div className="ava-article-body">{m.text || 'No content.'}</div>
          )}
        </div>
      )}
      {m.kind === 'job' && (
        <div className="ava-job-card">
          <div className="ava-job-hdr">
            <span className="ava-job-id">Job {m.job.jobId}</span>
            <span className={`ava-job-badge${m.job.overdue ? '' : ' green'}`}>{m.job.overdue ? 'Overdue' : 'On Track'}</span>
          </div>
          <div className="ava-job-rows">
            <div className="ava-job-row"><span className="ava-job-row-lbl">Client</span><span className="ava-job-row-val">{m.job.client || '—'}</span></div>
            <div className="ava-job-row"><span className="ava-job-row-lbl">Project</span><span className="ava-job-row-val">{m.job.project || '—'}</span></div>
            <div className="ava-job-row"><span className="ava-job-row-lbl">Received</span><span className="ava-job-row-val">{fmtDate(m.job.received)}</span></div>
            <div className="ava-job-row"><span className="ava-job-row-lbl">Due</span><span className="ava-job-row-val">{fmtDate(m.job.due)}</span></div>
            <div className="ava-job-row"><span className="ava-job-row-lbl">Samples</span><span className="ava-job-row-val">{m.job.sampleCount}</span></div>
            <div className="ava-job-row"><span className="ava-job-row-lbl">Tests</span><span className="ava-job-row-val">{m.job.resultsEntered} / {m.job.testCount} entered · {m.job.reported} reported</span></div>
            <div className="ava-job-row"><span className="ava-job-row-lbl">Current status</span><span className="ava-job-row-val">{m.job.currentStatus || '—'}</span></div>
            {(m.job.samplesOnHold > 0 || m.job.testsOnHold > 0) && (
              <div className="ava-job-row"><span className="ava-job-row-lbl">On hold</span><span className="ava-job-row-val">{m.job.samplesOnHold} sample(s) · {m.job.testsOnHold} test(s)</span></div>
            )}
          </div>
          <div className="ava-job-prog-wrap">
            <div className="ava-job-prog-bar">
              <div className="ava-job-prog-fill" style={{ width: `${m.job.progressPercent}%` }} />
            </div>
            <span className="ava-job-prog-pct">{m.job.progressPercent}% through the workflow</span>
          </div>
        </div>
      )}
      {m.kind === 'hits' && (
        <div className="ava-hits">
          {m.title && <div className="ava-hits-title">{m.title}</div>}
          {m.hits.length === 0 && <div className="ava-msg-bubble">No articles in this section.</div>}
          {m.hits.map((h) => (
            <button key={h.oid} className="ava-hit" onClick={() => openArticle(h.oid, h.topic)}>
              <div className="ava-hit-topic">{h.topic}</div>
              {h.preview && <div className="ava-hit-prev">{h.preview}</div>}
              {h.preview?.endsWith('…') && <div className="ava-hit-more">Read the full article ›</div>}
              {h.category && <div className="ava-hit-cat"><i className="ti ti-tag" /> {h.category}</div>}
            </button>
          ))}
        </div>
      )}
      {m.kind === 'samples' && (
        <div className="ava-table-wrap">
          <table className="ava-table">
            <thead><tr><th>Sample ID</th><th>Client Sample ID</th><th>Test</th><th>Method</th><th>Status</th></tr></thead>
            <tbody>
              {m.rows.map((r) => (
                <tr key={`${r.sampleId}|${r.test}|${r.method}`}>
                  <td>{r.sampleId}</td><td>{r.clientSampleId || '—'}</td><td>{r.test || '—'}</td><td>{r.method || '—'}</td>
                  <td>{r.status}{r.onHold && <span className="ava-hold"> · On hold</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {m.rows.length >= 50 && <div className="ava-hits-note">Showing the first 50 tests.</div>}
        </div>
      )}
      {m.kind === 'reports' && (
        <div className="ava-table-wrap">
          <table className="ava-table">
            <thead><tr><th>Report ID</th><th>Job ID</th><th>Status</th><th>Reported</th></tr></thead>
            <tbody>
              {m.rows.map((r) => (
                <tr key={r.reportId}><td>{r.reportId}</td><td>{r.jobId || '—'}</td><td>{r.status}</td><td>{fmtDate(r.reportedDate)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {m.kind === 'mywork' && (
        <AvaMyWorkCard work={m.work} onOpen={openScreen} onAsk={(q) => void ask(q)} />
      )}
      {m.kind === 'steps' && (
        <ol className="ava-steps">
          {m.steps.map((st, i) => <li key={`${i}|${st}`}>{st}</li>)}
        </ol>
      )}
      {m.kind === 'screens' && (
        <div className="ava-screens">
          {m.screens.map((s) => (
            <div key={`${s.id}|${s.path}`} className="ava-screen">
              <i className={`ti ${s.isMenu ? 'ti-folder' : 'ti-app-window'} ava-screen-ico`} />
              <div className="ava-screen-body">
                <div className="ava-screen-name">{s.name}{s.isMenu && <span className="ava-screen-tag">menu</span>}</div>
                <div className="ava-screen-path">{s.path}</div>
              </div>
              <button type="button" className="ava-screen-open" onClick={() => openScreen(s.id)}><i className="ti ti-external-link" /> Open</button>
            </div>
          ))}
        </div>
      )}
      {m.kind === 'form' && (
        <div className="ava-table-wrap">
          <div className="ava-hits-title">{m.form.screenName} — fields</div>
          <table className="ava-table">
            <thead><tr><th>Field</th><th>Required</th><th>Notes</th></tr></thead>
            <tbody>
              {[...m.form.fields].sort((a, b) => Number(b.required) - Number(a.required)).map((f) => (
                <tr key={f.key}>
                  <td>{f.label}</td>
                  <td>{f.required ? <span className="ava-req">Required</span> : '—'}</td>
                  <td>
                    {f.readOnly ? 'Filled in automatically'
                      : f.choices?.length ? `Choose: ${f.choices.slice(0, 8).join(', ')}${f.choices.length > 8 ? ', …' : ''}`
                      : f.choicesFromServer ? 'Pick from a list'
                      : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {m.kind === 'workflow' && (
        <div className="ava-flows">
          {m.next.map((n) => (
            <div key={`${n.module}|${n.flow}|${n.step}`} className="ava-flow">
              <div className="ava-flow-hdr">{n.module} <span className="ava-flow-sub">· {n.flow}</span></div>
              <ol className="ava-flow-steps">
                <li className="on">{n.step}</li>
                {n.next ? <li>{n.next}</li> : <li className="end">End of this flow</li>}
              </ol>
            </div>
          ))}
          {m.chains.map((c) => (
            <div key={`${c.module}|${c.flow}`} className="ava-flow">
              <div className="ava-flow-hdr">{c.module} <span className="ava-flow-sub">· {c.flow}</span></div>
              <ol className="ava-flow-steps">
                {c.steps.map((s, i) => <li key={`${i}|${s}`}>{s}</li>)}
              </ol>
            </div>
          ))}
        </div>
      )}
      {m.kind === 'capabilities' && (
        <div className="ava-caps">
          {m.items.map((c) => (
            <div key={c.title} className="ava-cap">
              <div className="ava-cap-title">{c.title}</div>
              <div className="ava-cap-desc">{c.description}</div>
              <div className="ava-suggest-list">
                {c.examples.map((q) => (
                  <button key={q} type="button" className="ava-suggest-chip" onClick={() => void ask(q)} disabled={busy}>{q}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {m.kind === 'worklist' && (
        <div className="ava-table-wrap">
          <table className="ava-table">
            <thead><tr><th>Job ID</th><th>Samples in queue</th><th>Due</th></tr></thead>
            <tbody>
              {m.list.jobs.map((j) => (
                <tr key={j.jobId}>
                  <td><button type="button" className="ava-link" onClick={() => ask(j.jobId)} title={`Show status of job ${j.jobId}`}>{j.jobId}</button></td>
                  <td>{j.sampleCount}</td><td>{fmtDate(j.due)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {m.list.jobCount > m.list.jobs.length && <div className="ava-hits-note">Showing {m.list.jobs.length} of {m.list.jobCount} jobs. Click a Job ID for its status.</div>}
        </div>
      )}
    </>
  );

  return (
    <div className={`ava-page${dark ? ' dark' : ''}`} style={themeStyle}>
      {/* ── LEFT SIDEBAR ── */}
      <aside className="ava-side">
        <div className="ava-side-brand">
          {/* AVA mascot, same as the header (user request 2026-10-03; was an "AL" tile). */}
          <div className="ava-side-brand-logo"><AvaFace /></div>
          <div className="ava-side-brand-name">ALPACA <span className="accent">LIMS</span></div>
        </div>
        <button className="ava-side-newchat" onClick={newChat}>
          <i className="ti ti-plus" /> New Chat
        </button>

        <div className="ava-side-scroll">
          {conversations.length === 0 ? (
            <>
              <div className="ava-side-sec">TODAY</div>
              <div className="ava-side-empty">No conversations yet</div>
            </>
          ) : (
            GROUP_ORDER.map((g) => {
              const inGroup = conversations.filter((c) => conversationGroup(c.updatedAt) === g);
              if (inGroup.length === 0) return null;
              return (
                <div key={g}>
                  <div className="ava-side-sec">{g}</div>
                  {inGroup.map((c) => (
                    <div key={c.id} className={`ava-side-conv${c.id === activeConvId ? ' active' : ''}`}>
                      <button type="button" className="ava-side-conv-open" onClick={() => openConversation(c)} title={c.title}>
                        <i className="ti ti-message-circle" />
                        <span>{c.title}</span>
                      </button>
                      <button type="button" className="ava-side-conv-del" onClick={() => void deleteConversation(c)}
                        title="Delete conversation" aria-label={`Delete conversation ${c.title}`}>
                        <i className="ti ti-trash" />
                      </button>
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>

        <div className="ava-side-sec">BROWSE HELP</div>
        <div className="ava-side-help">
          {SECTIONS.map((s) => (
            <button key={s.key}
              className={`ava-side-help-item${activeSection === s.label ? ' active' : ''}`}
              onClick={() => openSection(s.cat, s.label)}>
              <i className={`ti ${s.icon}`} />
              <span className="ava-side-help-name">{s.label}</span>
              <span className="ava-side-help-cnt">{counts ? (counts[s.key] ?? 0) : '·'}</span>
            </button>
          ))}
        </div>

        <button className="ava-side-rate" onClick={() => notify.info('Thanks for using AVA! Feedback feature coming soon.')}>
          <i className="ti ti-star" /> Rate AVA
        </button>
      </aside>

      {/* ── MAIN ── */}
      <main className="ava-main">
        {/* Top bar */}
        <header className="ava-top">
          <div className="ava-top-left">
            <div className="ava-top-avatar"><AvaFace /></div>
            <div>
              <div className="ava-top-name">AVA Insight <span className="ava-top-dot" /></div>
              <div className="ava-top-sub">ALPACA Virtual Assistant <span className="ava-top-ver">v1.0</span></div>
            </div>
          </div>
          <div className="ava-top-right">
            <button className="ava-top-newchat" onClick={newChat}><i className="ti ti-plus" /> New Chat</button>
            <div className="ava-menu-wrap" ref={menuRef}>
              <button className="ava-top-menu" onClick={() => setMenuOpen((o) => !o)} aria-label="Menu">
                <i className="ti ti-menu-2" />
              </button>
              {menuOpen && (
                <div className="ava-menu">
                  <button className="ava-menu-item" onClick={() => { setMenuOpen(false); notify.info(conversations.length ? `Chat history (${conversations.length}):\n` + conversations.map((c, i) => `${i + 1}. ${c.title}`).join('\n') : 'No conversations yet.'); }}>
                    <i className="ti ti-history" /> Chat History
                  </button>
                  <div className="ava-menu-sep" />
                  <button className="ava-menu-item" onClick={() => setDark((d) => !d)}>
                    <i className={`ti ${dark ? 'ti-sun' : 'ti-moon'}`} /> {dark ? 'Light' : 'Dark'}
                  </button>
                  <div className="ava-menu-item ava-menu-colors">
                    <i className="ti ti-palette" /> Color
                    <div className="ava-menu-palette">
                      {COLORS.map((c) => (
                        <button key={c} className={`ava-color-dot${accent === c ? ' on' : ''}`}
                          style={{ background: c }} aria-label={`Color ${c}`}
                          onClick={() => setAccent(c)} />
                      ))}
                    </div>
                  </div>
                  <div className="ava-menu-sep" />
                  <button className={`ava-menu-item${listening ? ' on' : ''}`} onClick={toggleVoiceChat}>
                    <i className={`ti ${listening ? 'ti-microphone-off' : 'ti-microphone'}`} /> Voice Chat {listening ? '(listening…)' : ''}
                  </button>
                  <button className={`ava-menu-item${voiceReply ? ' on' : ''}`} onClick={() => setVoiceReply((v) => !v)}>
                    <i className={`ti ${voiceReply ? 'ti-volume' : 'ti-volume-off'}`} /> Voice Reply {voiceReply ? '(on)' : ''}
                  </button>
                  <div className="ava-menu-sep" />
                  <button className="ava-menu-item" onClick={refreshAva}>
                    <i className="ti ti-refresh" /> Refresh
                  </button>
                  <button className="ava-menu-item" onClick={() => { clearChat(); setMenuOpen(false); }}>
                    <i className="ti ti-trash" /> Clear Chat
                  </button>
                  <div className="ava-menu-sep" />
                  {canSeeKnowledge && (
                    <button className="ava-menu-item" onClick={() => { setMenuOpen(false); setKnowledgeOpen(true); }}>
                      <i className="ti ti-brain" /> What AVA knows
                    </button>
                  )}
                  <button className="ava-menu-item" onClick={() => { setMenuOpen(false); openSection('Object Manuals', 'Object Manuals'); }}>
                    <i className="ti ti-book" /> AVA Manual
                  </button>
                  <button className="ava-menu-item" onClick={() => { setMenuOpen(false); ask('faq'); }}>
                    <i className="ti ti-help" /> Help
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content area */}
        <div className="ava-convo" ref={convoRef}>
          {showWelcome && (
            <div className="ava-welcome">
              {/* AVA mascot, same as the header (user request 2026-10-03; was a sparkles glyph). */}
              <div className="ava-welcome-icon"><AvaFace /></div>
              <h3>Hi {user?.name?.split(' ')[0] || 'there'} — I'm AVA</h3>
              <p>Ask a how-to question, “what is waiting for me?”, a Job / Sample / Report ID (e.g. <code>260514002</code>), or a count such as “how many samples are overdue”. Or pick a section from the left.</p>
              {myWork && (
                <div className="ava-welcome-mywork">
                  <AvaMyWorkCard work={myWork} onOpen={openScreen} onAsk={(q) => void ask(q)} />
                </div>
              )}
              <div className="ava-welcome-chips">
                {SECTIONS.map((s) => (
                  <button key={s.key} className="ava-welcome-chip" onClick={() => openSection(s.cat, s.label)}>
                    <i className={`ti ${s.icon}`} /> {s.label}
                    <span className="ava-welcome-chip-cnt">{counts ? (counts[s.key] ?? 0) : '·'}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {msgs.map((m) => (
            <div key={m.id} className={`ava-msg ${m.role}`}>
              <div className="ava-msg-avatar">{m.role === 'user' ? av : <AvaFace />}</div>
              <div className="ava-msg-body">
                <div className="ava-msg-meta">{m.role === 'user' ? 'You' : 'AVA'}</div>
                {m.kind === 'group'
                  ? <div className="ava-answer">{m.parts.map((p) => <div key={p.id} className="ava-answer-part">{renderPart(p)}</div>)}</div>
                  : renderPart(m)}
              </div>
            </div>
          ))}
          {busy && <div className="ava-msg bot"><div className="ava-msg-avatar"><AvaFace /></div><div className="ava-msg-body"><div className="ava-msg-bubble">…</div></div></div>}
        </div>

        {/* Bottom input */}
        <div className="ava-input-wrap">
          <form className="ava-input" onSubmit={(e) => { e.preventDefault(); ask(input); }}>
            <input
              placeholder='Ask AVA anything about ALPACA LIMS…'
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={busy}
            />
            <button type="button" className="ava-input-chip" onClick={() => ask('faq')} title="Show FAQs">
              <i className="ti ti-help-circle" /> FAQ
            </button>
            <button type="button" className={`ava-input-mic${listening ? ' on' : ''}`} onClick={toggleVoiceChat} aria-label="Voice">
              <i className={`ti ${listening ? 'ti-microphone-off' : 'ti-microphone'}`} />
            </button>
            <button className="ava-input-send" type="submit" disabled={busy || !input.trim()} aria-label="Send">
              <i className="ti ti-send" />
            </button>
          </form>
          {/* Single-line field: Enter sends (the old "Shift+Enter for new line" hint never worked). */}
          <div className="ava-input-hint">Press Enter to send</div>
        </div>
      </main>
      <AvaKnowledgeDialog open={knowledgeOpen} onClose={() => setKnowledgeOpen(false)} />
    </div>
  );
}
